import React, { useState, useEffect, useRef } from "react";
import { Input, Select, Button, Tag, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
  PrinterOutlined,
  ShoppingCartOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  POSCartItem,
  POSSale,
  POSPayment,
  PharmacyBatch,
} from "../../interfaces/POS";
import { PharmacyItem } from "../../interfaces/PharmacyItems";
import { Patient } from "../../interfaces/Patient";
import { getPharmacyItems } from "../../apis/pharmacyItems";
import { getPatients } from "../../apis/patient";
import { getUsers } from "../../apis/user";
import { createPOSSale } from "../../apis/pos";
import { useNotification } from "../../NotificationContext";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import PaymentModal, { PaymentData } from "../../components/PaymentModal";
import "react-datepicker/dist/react-datepicker.css";
import { User } from "../../interfaces/User";
import { log } from "console";

const { Option } = Select;

const PharmacyPOS: React.FC = () => {
  const { showNotification } = useNotification();
  const [searchQuery, setSearchQuery] = useState("");
  const [pharmacyItems, setPharmacyItems] = useState<PharmacyItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<PharmacyItem[]>([]);
  const [cart, setCart] = useState<POSCartItem[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [saleDate, setSaleDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState<string>("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [discount, setDiscount] = useState<number>(0); // Discount amount (will be converted to percentage)
  const [discountPercentage, setDiscountPercentage] = useState<number>(0); // Order level discount percentage
  const searchInputRef = useRef<any>(null);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [selectedItemForBatch, setSelectedItemForBatch] =
    useState<PharmacyItem | null>(null);
  const [selectedCartItemForBatch, setSelectedCartItemForBatch] = useState<
    string | null
  >(null);
  const [availableBatches, setAvailableBatches] = useState<PharmacyBatch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<PharmacyBatch | null>(
    null
  );
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);

  // Fetch pharmacy items
  useEffect(() => {
    fetchPharmacyItems();
    fetchPatients();
    fetchUsers();
  }, []);

  // Filter items based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredItems([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = pharmacyItems.filter(
      (item) =>
        (item.name ?? "").toLowerCase().includes(query) ||
    (item.sku ?? "").toLowerCase().includes(query) ||
    (item.barcode ?? "").toLowerCase().includes(query) ||
    (item.molecule ?? "").toLowerCase().includes(query)
    );
    setFilteredItems(filtered.slice(0, 10)); // Limit to 10 results
  }, [searchQuery, pharmacyItems]);

  const fetchPharmacyItems = async () => {
    try {
      const response: any = await getPharmacyItems({
        per_page: 1000,
        is_active: true,
      });
      // Handle the response structure: { data: [...], links: {...}, meta: {...} }
      const items = Array.isArray(response) ? response : response?.data || [];
      setPharmacyItems(items || []);
    } catch (error) {
      console.error("Error fetching pharmacy items:", error);
      showNotification(
        "error",
        "Failed to load pharmacy items",
        "",
        "topRight"
      );
    }
  };

  const fetchPatients = async () => {
    try {
      const response: any = await getPatients({ per_page: 100 });
      setPatients(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching patients:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response: any = await getUsers({ per_page: 100 });
      setUsers(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
  const userStr = localStorage.getItem("user");

  if (!userStr || users.length === 0) return;

  try {
    const loggedInUser: User = JSON.parse(userStr);

    const matchedUser = users.find(
      (u: User) => u.id === loggedInUser.id
    );

    if (matchedUser && matchedUser.id) {
      setSelectedUserId(matchedUser.id);
    }
  } catch (err) {
    console.error("Invalid user object in localStorage");
  }
}, [users]);

  // Get batches from pharmacy item instead of making API call
  const fetchBatchesForItem = (item: PharmacyItem) => {
    console.log({item});
  if (!item?.id) return;

  setIsLoadingBatches(true);

  try {
      // Get batches from the pharmacy item's batches array
      const batches = (item.batches || []).map((batch: any) => {
        // Map the batch structure to match PharmacyBatch interface
        // API returns camelCase, convert to snake_case for PharmacyBatch interface
        return {
          id: batch.id,
          batch_number: batch.batchNumber,
          manufacturing_date: batch.manufacturingDate,
          expiry_date: batch.expiryDate,
          purchase_price: batch.purchasePrice?.toString() || "0",
          selling_price: batch.sellingPrice?.toString() || "0",
          available_quantity: batch.availableQuantity,
          is_expired: batch.isExpired,
          is_expiring_soon: batch.isExpiringSoon,
          supplier_id: batch.supplierId,
          notes: batch.notes,
          is_active: batch.isActive,
          mrp: parseFloat(batch.sellingPrice || batch.purchasePrice || "0"),
        };
      }).filter((batch: any) => batch.available_quantity > 0 && batch.is_active && !batch.is_expired);
console.log({batches});

    setAvailableBatches(batches);

      // Auto-select if only one usable batch
    if (batches.length === 1) {
      setSelectedBatch(batches[0]);
    } else {
      setSelectedBatch(null);
    }
  } catch (error) {
      console.error("Error processing batches:", error);
    setAvailableBatches([]);
    setSelectedBatch(null);
  } finally {
    setIsLoadingBatches(false);
  }
};

  const calculateItemTotal = (item: POSCartItem): number => {
    const itemSubtotal = item.quantity * item.unitPrice;
    const itemDiscountAmount =
      itemSubtotal * ((item.discountPercentage || 0) / 100);
    const itemAfterDiscount = itemSubtotal - itemDiscountAmount;
    const itemTaxAmount = itemAfterDiscount * ((item.taxPercentage || 0) / 100);
    return itemAfterDiscount + itemTaxAmount;
  };

  const handleItemSelect = (item: PharmacyItem) => {
    // Check if item already exists in cart
    const existingItem = cart.find(
      (cartItem) => cartItem.pharmacyItemId === item.id
    );

    if (existingItem) {
      // Update quantity for existing item
      setCart(
        cart.map((cartItem) => {
          if (cartItem.id === existingItem.id) {
            const updatedItem = {
              ...cartItem,
              quantity: cartItem.quantity + 1,
            };
            return {
              ...updatedItem,
              total: calculateItemTotal(updatedItem),
            };
          }
          return cartItem;
        })
      );
      setSearchQuery("");
      setFilteredItems([]);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    } else {
      // Show batch selection modal for new item
      setSelectedItemForBatch(item);
      fetchBatchesForItem(item);
      setIsBatchModalOpen(true);
    }
  };

  const addToCartWithBatch = () => {
    if (!selectedItemForBatch) return;

    // Use selling_price from batch, fallback to 0 if not available
    const unitPrice = parseFloat(selectedBatch?.selling_price || "0") || 0;
    const mrp = selectedBatch?.mrp || unitPrice;

    // If updating an existing cart item
    if (selectedCartItemForBatch) {
      setCart(
        cart.map((cartItem) => {
          if (cartItem.id === selectedCartItemForBatch) {
            const updatedItem: POSCartItem = {
              ...cartItem,
              batchId: selectedBatch?.id,
              batchNumber: selectedBatch?.batch_number,
              expiryDate: selectedBatch?.expiry_date,
              unitPrice: unitPrice,
              mrp: mrp,
            };
            return {
              ...updatedItem,
              total: calculateItemTotal(updatedItem),
            };
          }
          return cartItem;
        })
      );
    } else {
      // Adding new item to cart
      const newCartItem: POSCartItem = {
        id: `item-${Date.now()}-${selectedItemForBatch.id}`,
        pharmacyItemId: selectedItemForBatch.id!,
        name: selectedItemForBatch.name,
        sku: selectedItemForBatch.sku,
        barcode: selectedItemForBatch.barcode,
        quantity: 1,
        unitPrice: unitPrice,
        mrp: mrp,
        discount: 0,
        discountPercentage: 0,
        tax: 0,
        taxPercentage: 0,
        total: unitPrice,
        form: selectedItemForBatch.form,
        strength: selectedItemForBatch.strength,
        unit: selectedItemForBatch.unit,
        batchId: selectedBatch?.id,
        batchNumber: selectedBatch?.batch_number,
        expiryDate: selectedBatch?.expiry_date,
      };

      setCart([...cart, newCartItem]);
    }

    // Reset batch selection
    setIsBatchModalOpen(false);
    setSelectedItemForBatch(null);
    setSelectedCartItemForBatch(null);
    setSelectedBatch(null);
    setAvailableBatches([]);
    setSearchQuery("");
    setFilteredItems([]);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const addToCart = (item: PharmacyItem) => {
    handleItemSelect(item);
  };

  const updateCartItemQuantity = (itemId: string, change: number) => {
    setCart(
      cart.map((item) => {
        if (item.id === itemId) {
          const newQuantity = Math.max(1, item.quantity + change);
          const updatedItem = {
            ...item,
            quantity: newQuantity,
          };
          return {
            ...updatedItem,
            total: calculateItemTotal(updatedItem),
          };
        }
        return item;
      })
    );
  };

  const updateCartItemPrice = (itemId: string, price: number) => {
    setCart(
      cart.map((item) => {
        if (item.id === itemId) {
          const updatedItem = {
            ...item,
            unitPrice: price,
          };
          return {
            ...updatedItem,
            total: calculateItemTotal(updatedItem),
          };
        }
        return item;
      })
    );
  };

  const removeFromCart = (itemId: string) => {
    setCart(cart.filter((item) => item.id !== itemId));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      return sum + itemSubtotal;
    }, 0);
  };

  const calculateTotalDiscount = () => {
    // Calculate item-level discounts
    const itemDiscounts = cart.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const itemDiscount =
        itemSubtotal * ((item.discountPercentage || 0) / 100);
      return sum + itemDiscount;
    }, 0);

    // Add order-level discount
    const subtotal = calculateSubtotal();
    const orderDiscount = subtotal * (discountPercentage / 100);

    return itemDiscounts + orderDiscount;
  };

  const calculateTotalTax = () => {
    return cart.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const itemDiscount =
        itemSubtotal * ((item.discountPercentage || 0) / 100);
      const itemAfterDiscount = itemSubtotal - itemDiscount;
      const itemTax = itemAfterDiscount * ((item.taxPercentage || 0) / 100);
      return sum + itemTax;
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const totalDiscount = calculateTotalDiscount();
    const totalTax = calculateTotalTax();
    return Math.max(0, subtotal - totalDiscount + totalTax);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      message.warning("Cart is empty. Please add items to cart.");
      return;
    }
    if (!selectedPatient?.id) {
      message.warning("Please select a patient before checkout.");
      return;
    }
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSave = async (paymentData: PaymentData) => {
    if (!selectedPatient?.id) {
      message.warning("Please select a patient");
      return;
    }

    if (cart.length === 0) {
      message.warning("Cart is empty. Please add items to cart.");
      return;
    }

    const subtotal = calculateSubtotal();
    const totalDiscount = calculateTotalDiscount();
    const totalTax = calculateTotalTax();
    const netAmount = subtotal - totalDiscount + totalTax;
    const totalPaid =
      (paymentData.upi || 0) +
      (paymentData.cash || 0) +
      (paymentData.card || 0) +
      (paymentData.cheque || 0) +
      (paymentData.online || 0);

    // if (totalPaid < netAmount) {
    //   message.warning(
    //     `Payment amount (₹${totalPaid}) is less than total (₹${netAmount})`
    //   );
    //   return;
    // }

    // Convert cart items to backend format
    const saleItems = cart.map((item) => {
      if (!item.batchId) {
        throw new Error(`Batch is required for item: ${item.name}`);
      }
      return {
        pharmacy_item_id: item.pharmacyItemId,
        pharmacy_batch_id: item.batchId,
        quantity: item.quantity,
        mrp: item.mrp || item.unitPrice,
        selling_price: item.unitPrice,
        discount_percentage: item.discountPercentage || 0,
        tax_percentage: item.taxPercentage || 0,
      };
    });

    // Prepare sale data matching backend API structure
    // Backend expects: patient_id, visit_id, doctor_id, sale_date, discount_percentage (order-level only), status, notes, items
    const saleData = {
      patient_id: selectedPatient?.id || null,
      visit_id: null, // Can be added later if needed
      doctor_id: selectedUserId || null,
      sale_date: saleDate.toISOString().split("T")[0],
      discount_percentage: discountPercentage || 0, // Order-level discount percentage (0-100)
      status: totalPaid >= netAmount ? "paid" : "partially_paid",
      notes: notes || paymentData.paymentNote || null,
      items: saleItems,
    };

    try {
      // Create the pharmacy sale
      // saleData is in backend format (snake_case) matching StorePharmacySaleRequest validation
      const response: any = await createPOSSale(saleData);
      const saleId = response?.data?.id || response?.id;

      if (!saleId) {
        throw new Error("Sale ID not returned from API");
      }

      // Create payment records using new format
      const { processPayment } = await import("../../apis/payment");
      
      // Build payments array with all payment modes
      const payments: any[] = [];
      const baseTimestamp = Date.now();
      
      if (paymentData.cash > 0) {
        payments.push({
          amount: paymentData.cash,
          payment_mode: "cash",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-CASH`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.upi > 0) {
        payments.push({
          amount: paymentData.upi,
          payment_mode: "upi",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-UPI`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.card > 0) {
        payments.push({
          amount: paymentData.card,
          payment_mode: "card",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-CARD`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.cheque > 0) {
        payments.push({
          amount: paymentData.cheque,
          payment_mode: "cheque",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-CHEQUE`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.online > 0) {
        payments.push({
          amount: paymentData.online,
          payment_mode: "online",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-ONLINE`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      // Process all payments in a single API call using new format
      if (payments.length > 0) {
        await processPayment({
          source_type: "pharmacy_sale",
          source_ids: [saleId],
          payments: payments,
          source_amounts: {
            [saleId]: netAmount,
          },
          invoice_date: saleDate.toISOString().split("T")[0],
        });
      }

      showNotification(
        "success",
        "Sale and payment completed successfully!",
        "",
        "topRight"
      );

      // Reset cart and form
      setCart([]);
      setSelectedPatient(null);
      setSelectedUserId(null);
      setDiscount(0);
      setDiscountPercentage(0);
      setNotes("");
      setSaleDate(new Date());
      setIsPaymentModalOpen(false);

      // Print receipt (optional)
      // if (response?.data || response) {
      //   printReceipt(response?.data || response);
      // }
    } catch (error: any) {
      console.error("Error creating sale:", error);
      showNotification(
        "error",
        error?.response?.data?.error?.details ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to complete sale",
        "",
        "topRight"
      );
    }
  };

  const handlePrintReceipt = () => {
    // This will be called from PaymentModal's print button
    // For now, we'll handle it after save
  };

  const printReceipt = (saleData: any) => {
    // Simple receipt printing (can be enhanced with a proper receipt component)
    const receiptWindow = window.open("", "_blank");
    if (receiptWindow) {
      const totalPaid =
        saleData.payments?.reduce(
          (sum: number, payment: any) => sum + (payment.amount || 0),
          0
        ) || 0;
      const total = saleData.total || calculateTotal();
      const receiptContent = `
        <html>
          <head>
            <title>Receipt</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .header { text-align: center; margin-bottom: 20px; }
              .item { margin: 10px 0; }
              .total { font-weight: bold; margin-top: 20px; }
              .footer { margin-top: 30px; text-align: center; font-size: 12px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>Pharmacy Receipt</h2>
              <p>Sale #${saleData.saleNumber || saleData.id}</p>
              <p>Date: ${format(
                new Date(saleData.saleDate || new Date()),
                "dd MMM yyyy, HH:mm"
              )}</p>
            </div>
            ${
              selectedPatient
                ? `<p><strong>Patient:</strong> ${selectedPatient.firstName} ${selectedPatient.lastName}</p>`
                : ""
            }
            <hr>
            ${(saleData.items || cart)
              .map(
                (item: any) => `
              <div class="item">
                <p><strong>${item.name}</strong> - ${item.quantity} x ₹${item.unitPrice} = ₹${item.total}</p>
              </div>
            `
              )
              .join("")}
            <hr>
            <div class="total">
              <p>Subtotal: ₹${saleData.subtotal || calculateSubtotal()}</p>
              ${
                (saleData.discount || discount) > 0
                  ? `<p>Discount: -₹${saleData.discount || discount}</p>`
                  : ""
              }
              <p>Total: ₹${total}</p>
              <p>Paid: ₹${totalPaid}</p>
              ${totalPaid > total ? `<p>Change: ₹${totalPaid - total}</p>` : ""}
            </div>
            <div class="footer">
              <p>Thank you for your purchase!</p>
            </div>
          </body>
        </html>
      `;
      receiptWindow.document.write(receiptContent);
      receiptWindow.document.close();
      receiptWindow.print();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && filteredItems.length > 0) {
      addToCart(filteredItems[0]);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Pharmacy POS</h1>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Product Search & Selection */}
        <div className="w-1/2 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200 space-y-4">
            {/* Date Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sale Date
              </label>
              <div className="w-full">
                <DatePicker
                  selected={saleDate}
                  onChange={(date: Date | null) =>
                    setSaleDate(date || new Date())
                  }
                  dateFormat="dd MMM yyyy"
                  className="w-full h-10 px-3 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            {/* Select Patient */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Patient
              </label>
              <Select
                showSearch
                size="large"
                style={{ width: "100%" }}
                placeholder="Search and select patient..."
                value={selectedPatient?.id || undefined}
                onChange={(value) => {
                  const patient = patients.find((p) => p.id === value);
                  setSelectedPatient(patient || null);
                }}
                filterOption={(input, option: any) => {
                  const patient = patients.find((p) => p.id === option?.value);
                  if (!patient) return false;
                  const searchText = input.toLowerCase();
                  const fullName = `${patient.firstName || ""} ${
                    patient.lastName || ""
                  }`.toLowerCase();
                  return (
                    fullName.includes(searchText) ||
                    (!!patient.phone &&
                      patient.phone.toLowerCase().includes(searchText)) ||
                    (!!patient.email &&
                      patient.email.toLowerCase().includes(searchText))
                  );
                }}
                allowClear
              >
                {patients.map((patient) => (
                  <Option key={patient.id} value={patient.id}>
                    {patient.firstName} {patient.lastName}{" "}
                    {patient.phone && `- ${patient.phone}`}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Select User */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select User
              </label>
              <Select
                showSearch
                size="large"
                style={{ width: "100%" }}
                placeholder="Search and select user..."
                value={selectedUserId || undefined}
                onChange={(value) => setSelectedUserId(value)}
                filterOption={(input, option: any) => {
                  const user = users.find((u) => u.id === option?.value);
                  if (!user) return false;
                  const searchText = input.toLowerCase();
                  const fullName = `${user.firstName || ""} ${
                    user.lastName || ""
                  }`.toLowerCase();
                  return (
                    fullName.includes(searchText) ||
                    (!!user.phone &&
                      user.phone.toLowerCase().includes(searchText)) ||
                    (!!user.email &&
                      user.email.toLowerCase().includes(searchText)) ||
                    (!!user.registrationNumber &&
                      user.registrationNumber
                        .toLowerCase()
                        .includes(searchText))
                  );
                }}
                allowClear
              >
                {users.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}{" "}
                    {user.phone && `- ${user.phone}`}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <Input.TextArea
                size="large"
                placeholder="Add notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Right Panel - Cart & Checkout */}
        <div className="w-1/2 bg-gray-50 flex flex-col">
          <div className="p-4 bg-white border-b border-gray-200 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <ShoppingCartOutlined />
              Cart ({cart.length})
            </h2>
            {/* Searchable Dropdown for Pharmacy Items */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add Pharmacy Item
              </label>
              <Select
                showSearch
                size="large"
                style={{ width: "100%" }}
                placeholder="Search and select pharmacy item to add..."
                value={undefined}
                onChange={(value) => {
                  const selectedItem = pharmacyItems.find(
                    (pi) => pi.id === value
                  );
                  if (selectedItem) {
                    handleItemSelect(selectedItem);
                  }
                }}
                filterOption={(input, option: any) => {
                  const selectedItem = pharmacyItems.find(
                    (pi) => pi.id === option?.value
                  );
                  if (!selectedItem) return false;
                  const searchText = input.toLowerCase();
                  return (
                    selectedItem.name.toLowerCase().includes(searchText) ||
                    selectedItem.sku.toLowerCase().includes(searchText) ||
                    (!!selectedItem.barcode &&
                      selectedItem.barcode
                        .toLowerCase()
                        .includes(searchText)) ||
                    (!!selectedItem.molecule &&
                      selectedItem.molecule.toLowerCase().includes(searchText))
                  );
                }}
              >
                {pharmacyItems.map((pharmacyItem) => (
                  <Option key={pharmacyItem.id} value={pharmacyItem.id}>
                    {pharmacyItem.name} - {pharmacyItem.sku}
                    {pharmacyItem.form && ` (${pharmacyItem.form})`}
                    {pharmacyItem.strength && ` - ${pharmacyItem.strength}`}
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                Cart is empty. Add items to get started.
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((item) => {
                  const itemData = pharmacyItems.find(
                    (pi) => pi.id === item.pharmacyItemId
                  );
                  return (
                    <div
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">
                            {item.name}
                          </h3>
                          <div className="text-sm text-gray-600 mt-1">
                            {item.sku} {item.form && `• ${item.form}`}
                            {item.strength && ` • ${item.strength}`}
                          </div>
                          {item.batchNumber && (
                            <div className="text-xs text-gray-500 mt-1">
                              <Tag color="blue">Batch: {item.batchNumber}</Tag>
                              {item.expiryDate && (
                                <span className="ml-2">
                                  • Exp:{" "}
                                  {format(
                                    new Date(item.expiryDate),
                                    "MMM yyyy"
                                  )}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeFromCart(item.id)}
                        />
                      </div>

                      {/* Item and Batch Selection */}
                      <div className="mb-3 space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Pharmacy Item
                          </label>
                          <Select
                            showSearch
                            style={{ width: "100%" }}
                            value={item.pharmacyItemId}
                            onChange={(value) => {
                              const selectedItem = pharmacyItems.find(
                                (pi) => pi.id === value
                              );
                              if (selectedItem) {
                                handleItemSelect(selectedItem);
                                removeFromCart(item.id);
                              }
                            }}
                            filterOption={(input, option: any) => {
                              const selectedItem = pharmacyItems.find(
                                (pi) => pi.id === option?.value
                              );
                              if (!selectedItem) return false;
                              const searchText = input.toLowerCase();
                              return (
                                selectedItem.name
                                  .toLowerCase()
                                  .includes(searchText) ||
                                selectedItem.sku
                                  .toLowerCase()
                                  .includes(searchText)
                              );
                            }}
                            placeholder="Select pharmacy item"
                          >
                            {pharmacyItems.map((pharmacyItem) => (
                              <Option
                                key={pharmacyItem.id}
                                value={pharmacyItem.id}
                              >
                                {pharmacyItem.name} - {pharmacyItem.sku}
                              </Option>
                            ))}
                          </Select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Batch
                          </label>
                          <Button
                            size="small"
                            onClick={() => {
                              if (itemData) {
                                setSelectedItemForBatch(itemData);
                                setSelectedCartItemForBatch(item.id);
                                fetchBatchesForItem(itemData);
                                setIsBatchModalOpen(true);
                              }
                            }}
                            className="w-full text-left"
                          >
                            {item.batchNumber
                              ? `Batch: ${item.batchNumber}`
                              : "Select Batch"}
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2">
                          <Button
                            size="small"
                            icon={<MinusOutlined />}
                            onClick={() => updateCartItemQuantity(item.id, -1)}
                          />
                          <Input
                            type="number"
                            min={1}
                            value={item.quantity}
                            onChange={(e) => {
                              const qty = parseInt(e.target.value) || 1;
                              setCart(
                                cart.map((cartItem) => {
                                  if (cartItem.id === item.id) {
                                    const updatedItem = {
                                      ...cartItem,
                                      quantity: qty,
                                    };
                                    return {
                                      ...updatedItem,
                                      total: calculateItemTotal(updatedItem),
                                    };
                                  }
                                  return cartItem;
                                })
                              );
                            }}
                            className="w-16 text-center"
                          />
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => updateCartItemQuantity(item.id, 1)}
                          />
                        </div>

                        <div className="flex-1">
                          <Input
                            type="number"
                            prefix="₹"
                            value={item.unitPrice}
                            onChange={(e) =>
                              updateCartItemPrice(
                                item.id,
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="w-full"
                            placeholder="Price"
                          />
                        </div>

                        <div className="text-right">
                          <div className="font-semibold text-gray-900">
                            ₹{item.total}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.quantity} × ₹{item.unitPrice}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cart Summary */}
          <div className="bg-white border-t border-gray-200 p-4">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal:</span>
                <span>₹{calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Tax:</span>
                <span>₹{calculateTotalTax().toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Discount (%):</span>
                <Input
                  type="number"
                  suffix="%"
                  value={discountPercentage}
                  onChange={(e) => {
                    const percentage = parseFloat(e.target.value) || 0;
                    setDiscountPercentage(
                      Math.min(100, Math.max(0, percentage))
                    );
                    const subtotal = calculateSubtotal();
                    setDiscount(subtotal * (percentage / 100));
                  }}
                  className="w-32"
                  placeholder="0"
                  min={0}
                  max={100}
                />
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Discount Amount:</span>
                <span>₹{calculateTotalDiscount().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
                <span>Total:</span>
                <span>₹{calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              block
              onClick={handleCheckout}
              disabled={cart.length === 0}
              className="bg-primary hover:bg-hover"
            >
              Checkout
            </Button>
          </div>
        </div>
      </div>

      {/* Batch Selection Modal */}
      {isBatchModalOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              aria-hidden="true"
              onClick={() => {
                setIsBatchModalOpen(false);
                setSelectedItemForBatch(null);
                setSelectedCartItemForBatch(null);
                setSelectedBatch(null);
                setAvailableBatches([]);
              }}
            ></div>

            {/* Modal */}
            <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  Select Batch - {selectedItemForBatch?.name}
                </h2>
                <button
                  onClick={() => {
                    setIsBatchModalOpen(false);
                    setSelectedItemForBatch(null);
                    setSelectedCartItemForBatch(null);
                    setSelectedBatch(null);
                    setAvailableBatches([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <CloseOutlined className="text-xl" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                {isLoadingBatches ? (
                  <div className="text-center py-8">Loading batches...</div>
                ) : availableBatches.length === 0 ? (
                  <div className="space-y-4">
                    <div className="text-center py-4 text-gray-500">
                      No batches available for this item. You can still add it
                      without a batch.
                    </div>
                    <Button
                      type="primary"
                      block
                      onClick={() => {
                        setSelectedBatch(null);
                        addToCartWithBatch();
                      }}
                      className="bg-primary hover:bg-hover"
                    >
                      Add Without Batch
                    </Button>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    <div className="space-y-3">
                      {availableBatches.map((batch: PharmacyBatch) => {
                        const expiryDate = batch.expiry_date;
                        const mfgDate = batch.manufacturing_date;
                        const availableQty = batch.available_quantity || 0;
                        const sellingPrice = parseFloat(
                          batch.selling_price || "0"
                        );
                        const mrp = batch.mrp || sellingPrice;
                        const isExpired =
                          expiryDate && new Date(expiryDate) < new Date();
                        const isLowStock = availableQty <= 10;

                        return (
                          <div
                            key={batch.id}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              selectedBatch?.id === batch.id
                                ? "border-primary bg-blue-50"
                                : "border-gray-200 hover:border-gray-300"
                            } ${isExpired ? "bg-red-50 border-red-300" : ""}`}
                            onClick={() => setSelectedBatch(batch)}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-gray-900">
                                    Batch: {batch.batch_number}
                                  </h4>
                                  {isExpired && <Tag color="red">Expired</Tag>}
                                  {isLowStock && !isExpired && (
                                    <Tag color="orange">Low Stock</Tag>
                                  )}
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <span className="text-gray-600">
                                      Available Qty:
                                    </span>
                                    <span className="ml-2 font-medium text-gray-900">
                                      {availableQty}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">MRP:</span>
                                    <span className="ml-2 font-medium text-gray-900">
                                      ₹{mrp.toFixed(2)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">
                                      Selling Price:
                                    </span>
                                    <span className="ml-2 font-medium text-primary">
                                      ₹{sellingPrice.toFixed(2)}
                                    </span>
                                  </div>
                                  {expiryDate && (
                                    <div>
                                      <span className="text-gray-600">
                                        Expiry Date:
                                      </span>
                                      <span
                                        className={`ml-2 font-medium ${
                                          isExpired
                                            ? "text-red-600"
                                            : "text-gray-900"
                                        }`}
                                      >
                                        {format(
                                          new Date(expiryDate),
                                          "dd MMM yyyy"
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  {mfgDate && (
                                    <div>
                                      <span className="text-gray-600">
                                        Mfg Date:
                                      </span>
                                      <span className="ml-2 font-medium text-gray-900">
                                        {format(
                                          new Date(mfgDate),
                                          "dd MMM yyyy"
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  {batch.purchase_price && (
                                    <div>
                                      <span className="text-gray-600">
                                        Purchase Price:
                                      </span>
                                      <span className="ml-2 font-medium text-gray-900">
                                        ₹
                                        {parseFloat(
                                          batch.purchase_price || "0"
                                        ).toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="ml-4">
                                {selectedBatch?.id === batch.id && (
                                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                    <span className="text-white text-xs">
                                      ✓
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <Button
                  onClick={() => {
                    setIsBatchModalOpen(false);
                    setSelectedItemForBatch(null);
                    setSelectedCartItemForBatch(null);
                    setSelectedBatch(null);
                    setAvailableBatches([]);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={addToCartWithBatch}
                  disabled={!selectedBatch && availableBatches.length > 0}
                  className="px-6 py-2 text-white bg-primary hover:bg-hover rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {selectedCartItemForBatch ? "Update Batch" : "Add to Cart"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        total={calculateTotal()}
        discount={calculateTotalDiscount()}
        sourceType="pharmacy"
        sourceId={selectedUserId ?? 0}
        patientId={selectedPatient?.id}
        patientName={
          selectedPatient
            ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
            : "Counter Sale"
        }
        onSave={handlePaymentSave}
        // onPrint={handlePrintReceipt}
      />
    </div>
  );
};

export default PharmacyPOS;
